/**
 * Activation-event helper (T204).
 *
 *   trackEvent("first_match_scored", { match_id })
 *
 * Persona is auto-detected from the current SPA route via detectPersona.
 * Idempotency for "first X" semantics is enforced client-side via
 * localStorage — we don't POST repeats. Server-side dedup happens
 * naturally because the model is append-only and analytics consumers
 * just look for `EXISTS` per (user, event_key).
 *
 * Failure mode: silent. Logging is best-effort; never block the user
 * action on the analytics POST.
 */
import api from "../api/axios";
import { detectPersona } from "./detectPersona";


const FIRED_KEY = "zcd_evt_fired_v1";


function loadFired() {
  try {
    const raw = localStorage.getItem(FIRED_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch { return new Set(); }
}


function saveFired(set) {
  try { localStorage.setItem(FIRED_KEY, JSON.stringify([...set])); } catch {}
}


/**
 * Log an activation event. Pass `once: true` to dedupe client-side
 * (the helper will skip repeat POSTs for the same event_key on the
 * same browser).
 *
 * @param {string} event_key  — stable key, e.g. "first_match_scored"
 * @param {object} metadata   — small JSON payload, ≤4KB
 * @param {object} opts       — { once: boolean, persona: string }
 */
export function trackEvent(event_key, metadata = {}, opts = {}) {
  if (!event_key) return Promise.resolve(null);
  if (opts.once) {
    const fired = loadFired();
    if (fired.has(event_key)) return Promise.resolve(null);
    fired.add(event_key);
    saveFired(fired);
  }
  const persona = opts.persona || detectPersona();
  return api.post("/api/analytics/event/", {
    persona,
    event_key,
    metadata,
  }).catch(() => null);
}
